// src/pages/employeeCommission/EmployeeCommissionReport.jsx
import React, { useState, useEffect } from 'react';
import { BarChart2, Download, FileText, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  generateCommissionReport,
  getEmployeeDetailedOrders,
  getCommissionSettings,
} from '../../services/api/employeeCommission';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './EmployeeCommissionReport.module.css';

const LS_KEY = 'commissionReport_timeRange';

// ─── Period generators ────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Generate period options based on timeRange.
 * Each option: { key: string, label: string, dateFrom: string, dateTo: string }
 * cutoffDay: 1–28 for custom monthly cutoff, or null for end-of-month.
 */
const buildPeriodOptions = (timeRange, cutoffDay = null) => {
  const now = new Date();
  const options = [];

  if (timeRange === 'monthly') {
    const months = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December',
    ];

    if (cutoffDay && cutoffDay >= 1 && cutoffDay <= 28) {
      // Cutoff-based monthly periods
      let currentFromMonth = now.getMonth();
      let currentFromYear  = now.getFullYear();
      if (now.getDate() <= cutoffDay) {
        currentFromMonth -= 1;
        if (currentFromMonth < 0) { currentFromMonth = 11; currentFromYear -= 1; }
      }

      for (let i = 0; i < 5; i++) {
        let fromMonth = currentFromMonth - i;
        let fromYear  = currentFromYear;
        while (fromMonth < 0) { fromMonth += 12; fromYear -= 1; }

        const from  = new Date(fromYear, fromMonth, cutoffDay + 1);
        const to    = new Date(fromYear, fromMonth + 1, cutoffDay);
        const label = i === 0
          ? `Current Period (${fmt(from)} – ${fmt(to)})`
          : `${fmt(from)} – ${fmt(to)}`;
        options.push({ key: `mc_${i}`, label, dateFrom: fmt(from), dateTo: fmt(to) });
      }
      return options;
    }

    // Standard end-of-month periods
    for (let i = 0; i < 5; i++) {
      const year  = now.getMonth() - i < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const month = ((now.getMonth() - i) + 12) % 12;
      const from  = new Date(year, month, 1);
      const to    = new Date(year, month + 1, 0);
      const label = i === 0
        ? `This Month (${months[month]})`
        : i === 1
        ? `Last Month (${months[month]})`
        : months[month];
      options.push({ key: `m_${i}`, label, dateFrom: fmt(from), dateTo: fmt(to) });
    }
    return options;
  }

  if (timeRange === 'fortnightly') {
    // Walk back 6 fortnights from current fortnight
    let year  = now.getFullYear();
    let month = now.getMonth();
    let isFirstHalf = now.getDate() <= 15;

    for (let i = 0; i < 6; i++) {
      let from, to;
      if (isFirstHalf) {
        from = new Date(year, month, 1);
        to   = new Date(year, month, 15);
      } else {
        from = new Date(year, month, 16);
        to   = new Date(year, month + 1, 0);
      }
      const label = i === 0
        ? `Current (${fmt(from)} – ${fmt(to)})`
        : `${fmt(from)} – ${fmt(to)}`;
      options.push({ key: `fn_${i}`, label, dateFrom: fmt(from), dateTo: fmt(to) });

      // Step back
      if (isFirstHalf) {
        month -= 1;
        if (month < 0) { month = 11; year -= 1; }
        isFirstHalf = false;
      } else {
        isFirstHalf = true;
      }
    }
    return options;
  }

  if (timeRange === 'weekly') {
    // Walk back 12 weeks from current week (Mon–Sun)
    const day = now.getDay();
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    const currentMonday = new Date(now);
    currentMonday.setDate(now.getDate() + diffToMonday);
    currentMonday.setHours(0, 0, 0, 0);

    for (let i = 0; i < 12; i++) {
      const monday = new Date(currentMonday);
      monday.setDate(currentMonday.getDate() - i * 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const label = i === 0
        ? `Current Week (${fmt(monday)} – ${fmt(sunday)})`
        : `${fmt(monday)} – ${fmt(sunday)}`;
      options.push({ key: `w_${i}`, label, dateFrom: fmt(monday), dateTo: fmt(sunday) });
    }
    return options;
  }

  return options;
};

// ─── Formatters ───────────────────────────────────────────────────────────────

const formatLKR = (amount) => {
  const num = parseFloat(amount) || 0;
  return `LKR ${num.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const basisLabel = (key) => {
  const map = { order_count: 'Per Order', order_quantity: 'Per Qty', order_value: '% Value' };
  return map[key] || key;
};

const formatRate = (rate, basis) => {
  const val = parseFloat(rate).toFixed(2);
  return basis === 'order_value' ? `${val}%` : `LKR ${val}`;
};

const TIME_RANGE_OPTIONS = [
  { value: 'monthly',     label: 'Monthly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'weekly',      label: 'Weekly' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const EmployeeCommissionReport = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  // Persist timeRange in localStorage
  const [timeRange, setTimeRange] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || 'monthly'; } catch { return 'monthly'; }
  });

  const [cutoffDay, setCutoffDay] = useState(null);
  const [cutoffLoaded, setCutoffLoaded] = useState(false);

  // Load cutoff day on mount
  useEffect(() => {
    getCommissionSettings().then((settings) => {
      const saved = settings.monthlyCutoffDate;
      setCutoffDay((saved && saved >= 1 && saved <= 28) ? saved : null);
    }).catch(() => {}).finally(() => setCutoffLoaded(true));
  }, []);

  const periodOptions = cutoffLoaded ? buildPeriodOptions(timeRange, cutoffDay) : [];

  const [selectedPeriodKey, setSelectedPeriodKey] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  // When timeRange or cutoffDay changes, reset selected period and persist
  useEffect(() => {
    if (!cutoffLoaded) return;
    try { localStorage.setItem(LS_KEY, timeRange); } catch {}
    const opts = buildPeriodOptions(timeRange, cutoffDay);
    setSelectedPeriodKey(opts[0]?.key || '');
    setReportData(null);
  }, [timeRange, cutoffDay, cutoffLoaded]);

  const selectedPeriod = periodOptions.find((o) => o.key === selectedPeriodKey);

  const handleGenerate = async () => {
    if (!selectedPeriod) return;
    setLoading(true);
    try {
      const res = await generateCommissionReport({
        dateFrom:  selectedPeriod.dateFrom,
        dateTo:    selectedPeriod.dateTo,
        timeRange,
      });
      setReportData(res);
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.reportGenerateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportSummary = () => {
    if (!reportData?.employees?.length) return;

    const data = reportData.employees.map((e) => ({
      ID: e.userId,
      Name: e.user?.name || '',
      'Employee #': e.user?.employeeNumber || '',
      'Basis': basisLabel(e.calculationBasis),
      'Confirmed Orders': e.confirmedOrderCount || 0,
      'Confirmed Order Value (LKR)': (e.confirmedOrderValue || 0).toFixed(2),
      'Confirmed Item Qty': e.confirmedOrderQuantity || 0,
      'Confirm Rate': formatRate(e.confirmCommission, e.calculationBasis),
      'Create Rate': formatRate(e.createCommission, e.calculationBasis),
      'Total Confirm Commission (LKR)': (e.totalConfirmCommission || 0).toFixed(2),
      'Total Create Commission (LKR)': (e.totalCreateCommission || 0).toFixed(2),
      'Total Commission (LKR)': ((e.totalConfirmCommission || 0) + (e.totalCreateCommission || 0)).toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commission Summary');
    XLSX.writeFile(wb, `commission_report_${reportData.dateFrom}_${reportData.dateTo}.xlsx`);
  };

  const handleDownloadDetail = async (employee) => {
    if (!selectedPeriod) return;
    setDownloadingId(employee.userId);
    try {
      const res = await getEmployeeDetailedOrders(employee.userId, {
        dateFrom: selectedPeriod.dateFrom,
        dateTo:   selectedPeriod.dateTo,
      });

      const rows = res.orders.map((o) => ({
        'Order Number':           o.orderNumber,
        'Airwaybill':             o.airwaybillNumber,
        'Courier':                o.businessCourierName,
        'Items':                  o.itemsDescription,
        'Created':                o.created  ? 'Yes' : 'No',
        'Confirmed':              o.confirmed ? 'Yes' : 'No',
        'Grand Total (LKR)':      o.grandTotal?.toFixed(2),
        'Total Qty':              o.totalQty,
        'Confirm Commission (LKR)': o.confirmCommission?.toFixed(2),
        'Create Commission (LKR)':  o.createCommission?.toFixed(2),
        'Total Commission (LKR)':   o.totalCommission?.toFixed(2),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Commission Detail');
      XLSX.writeFile(
        wb,
        `commission_detail_${res.employee?.name || employee.userId}_${selectedPeriod.dateFrom}_${selectedPeriod.dateTo}.xlsx`
      );
    } catch {
      showError(t('employeeCommission.detailExportFailed'));
    } finally {
      setDownloadingId(null);
    }
  };

  // Summary stats
  const totalCommission = reportData?.employees?.reduce(
    (sum, e) => sum + (e.totalConfirmCommission || 0) + (e.totalCreateCommission || 0), 0
  ) || 0;
  const totalEmployees = reportData?.employees?.length || 0;
  const totalOrders    = reportData?.employees?.reduce(
    (sum, e) => sum + (e.confirmedOrderCount || 0) + (e.createdOrderCount || 0), 0
  ) || 0;

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('employeeCommission.commissionReportTitle')}</h1>
        <p className={styles.pageSubtitle}>{t('employeeCommission.commissionReportSubtitle')}</p>
      </div>

      {/* Filter Card */}
      <div className={styles.filterCard}>
        {/* Time range selector */}
        <div style={{ marginBottom: '1rem' }}>
          <p className={styles.filterLabel}>Commission Period</p>
          <div className={styles.rangePills}>
            {TIME_RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.rangePill} ${timeRange === opt.value ? styles.rangePillActive : ''}`}
                onClick={() => setTimeRange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date range selector based on timeRange */}
        <div style={{ marginBottom: '1rem' }}>
          <p className={styles.filterLabel}>{t('employeeCommission.selectDateRange')}</p>
          <div className={styles.rangePills}>
            {periodOptions.map((opt) => (
              <button
                key={opt.key}
                className={`${styles.rangePill} ${selectedPeriodKey === opt.key ? styles.rangePillActive : ''}`}
                onClick={() => setSelectedPeriodKey(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button className={styles.btnPrimary} onClick={handleGenerate} disabled={loading || !selectedPeriod}>
          {loading ? (
            <><div className={styles.spinner} />{t('employeeCommission.generating')}</>
          ) : (
            <><RefreshCw size={15} />{t('employeeCommission.generateReport')}</>
          )}
        </button>
      </div>

      {/* Summary Stats */}
      {reportData && (
        <>
          <div className={styles.summaryRow}>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Commissioned Employees</p>
              <p className={styles.summaryValue}>{totalEmployees}</p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Total Commissions</p>
              <p className={`${styles.summaryValue} ${styles.summaryValueSmall}`} style={{ color: 'var(--color-primary)' }}>
                {formatLKR(totalCommission)}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Total Order Appearances</p>
              <p className={styles.summaryValue}>{totalOrders}</p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Report Period</p>
              <p className={styles.summaryValue} style={{ fontSize: '0.9375rem' }}>
                {reportData.dateFrom} → {reportData.dateTo}
              </p>
            </div>
          </div>

          {/* Report Table */}
          <div className={styles.tableCard}>
            <div className={styles.tableCardHeader}>
              <h3 className={styles.tableCardTitle}>Commission Breakdown by Employee</h3>
              <button className={styles.btnExport} onClick={handleExportSummary}>
                <Download size={15} />
                Export Summary Excel
              </button>
            </div>

            {reportData.employees.length === 0 ? (
              <div className={styles.emptyState}>
                <BarChart2 size={48} />
                <p className={styles.emptyTitle}>{t('employeeCommission.noReportData')}</p>
                <p className={styles.emptyDesc}>{t('employeeCommission.noReportDataDesc')}</p>
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.reportTable}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Employee</th>
                      <th>Emp #</th>
                      <th>Basis</th>
                      <th>Confirmed Orders</th>
                      <th>Confirmed Value</th>
                      <th>Confirm Rate</th>
                      <th>Total Confirm</th>
                      <th>Created Orders</th>
                      <th>Create Rate</th>
                      <th>Total Create</th>
                      <th>Total Commission</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.employees.map((e) => {
                      const total = (e.totalConfirmCommission || 0) + (e.totalCreateCommission || 0);
                      const isDownloading = downloadingId === e.userId;
                      return (
                        <tr key={e.userId}>
                          <td style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>{e.userId}</td>
                          <td style={{ fontWeight: 600 }}>{e.user?.name || '—'}</td>
                          <td style={{ color: 'var(--color-text-secondary)' }}>{e.user?.employeeNumber || '—'}</td>
                          <td>
                            <span style={{
                              padding: '0.2rem 0.5rem',
                              background: 'var(--color-primary-bg)',
                              color: 'var(--color-primary-dark)',
                              borderRadius: 6,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                            }}>
                              {basisLabel(e.calculationBasis)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{e.confirmedOrderCount || 0}</td>
                          <td className={styles.subCommission}>{formatLKR(e.confirmedOrderValue)}</td>
                          <td className={styles.subCommission}>{formatRate(e.confirmCommission, e.calculationBasis)}</td>
                          <td className={styles.subCommission}>{formatLKR(e.totalConfirmCommission)}</td>
                          <td style={{ textAlign: 'center' }}>{e.createdOrderCount || 0}</td>
                          <td className={styles.subCommission}>{formatRate(e.createCommission, e.calculationBasis)}</td>
                          <td className={styles.subCommission}>{formatLKR(e.totalCreateCommission)}</td>
                          <td>
                            <span className={styles.totalCommission}>{formatLKR(total)}</span>
                          </td>
                          <td>
                            <button
                              className={styles.btnOutline}
                              onClick={() => handleDownloadDetail(e)}
                              disabled={isDownloading}
                            >
                              {isDownloading ? (
                                <div className={styles.spinnerDark} />
                              ) : (
                                <FileText size={13} />
                              )}
                              {t('employeeCommission.downloadDetail')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!reportData && !loading && (
        <div className={styles.tableCard}>
          <div className={styles.emptyState}>
            <BarChart2 size={48} />
            <p className={styles.emptyTitle}>Select a period and generate the report</p>
          </div>
        </div>
      )}

      {loading && (
        <div className={styles.tableCard}>
          <div className={styles.loadingState}>
            <div className={styles.spinnerDark} />
            <span>{t('employeeCommission.loadingReport')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeCommissionReport;